import { campaigns } from "./data";
import type { CampaignDefinition, CampaignFocus, EmailStep, Lead, LeadStatus, ScoreResult, StudioSettings } from "./types";

const roleTerms = [
  "realtor",
  "real estate agent",
  "agent",
  "broker",
  "team lead",
  "owner",
  "founder",
  "partner",
  "principal",
  "agency owner",
  "marketing",
  "brand",
  "operations",
  "consultant",
  "coach",
  "director",
];

const localMarketTerms = [
  "gwinnett",
  "lawrenceville",
  "duluth",
  "suwanee",
  "peachtree corners",
  "sugar hill",
  "buford",
  "snellville",
  "norcross",
  "lilburn",
  "dacula",
  "grayson",
  "loganville",
];

const smallBusinessTerms = [
  "realtor",
  "real estate",
  "mortgage",
  "broker",
  "insurance",
  "consultant",
  "coach",
  "freelance",
  "salon",
  "spa",
  "fitness",
  "studio",
  "organizer",
  "contractor",
  "home service",
  "agency",
  "owner",
];

const statusLabels: Record<LeadStatus, string> = {
  new: "New",
  research: "Research",
  ready: "Ready",
  sent: "Sent",
  replied: "Replied",
  booked: "Booked",
  do_not_contact: "DNC",
};

const csvAliases: Record<keyof Omit<Lead, "id" | "lastTouch">, string[]> = {
  company: ["company", "business", "businessname", "organization", "account", "name"],
  website: ["website", "domain", "url", "companywebsite"],
  industry: ["industry", "category", "vertical", "businesscategory"],
  city: ["city", "locality"],
  state: ["state", "region", "province"],
  contactName: ["contact", "contactname", "fullname", "nameperson", "person", "leadname"],
  title: ["title", "jobtitle", "role", "position"],
  email: ["email", "workemail", "emailaddress", "personemail"],
  phone: ["phone", "phonenumber", "businessphone"],
  linkedin: ["linkedin", "linkedinurl", "profile"],
  notes: ["notes", "note", "description", "bio"],
  signals: ["signals", "trigger", "triggers", "intent", "personalization", "research"],
  source: ["source", "datasource", "origin"],
  status: ["status", "stage"],
};

export function statusLabel(status: LeadStatus) {
  return statusLabels[status];
}

export function getCampaign(id: CampaignFocus): CampaignDefinition {
  return campaigns.find((campaign) => campaign.id === id) ?? campaigns[0];
}

export function getLeadText(lead: Lead) {
  return [
    lead.company,
    lead.website,
    lead.industry,
    lead.city,
    lead.state,
    lead.contactName,
    lead.title,
    lead.notes,
    lead.signals,
  ]
    .join(" ")
    .toLowerCase();
}

export function scoreLead(lead: Lead, campaign: CampaignDefinition): ScoreResult {
  const text = getLeadText(lead);
  const reasons: string[] = [];
  let score = 28;

  if (smallBusinessTerms.some((term) => text.includes(term))) {
    score += 12;
    reasons.push("small business fit");
  }

  if (localMarketTerms.some((term) => text.includes(term)) || lead.state.toLowerCase() === "ga") {
    score += 10;
    reasons.push("local market");
  }

  const accountTerms = campaign.idealAccounts
    .flatMap((term) => term.toLowerCase().split(/,| and |\/| with | for /g))
    .map((term) => term.trim())
    .filter((term) => term.length > 4);

  if (accountTerms.some((term) => text.includes(term))) {
    score += 14;
    reasons.push("ICP match");
  }

  if (campaign.triggers.some((trigger) => text.includes(trigger.toLowerCase()))) {
    score += 20;
    reasons.push("trigger found");
  }

  if (lead.website) {
    score += 8;
    reasons.push("website found");
  } else {
    score -= 10;
    reasons.push("needs website");
  }

  if (lead.email) {
    score += 14;
    reasons.push("email ready");
  } else {
    score -= 16;
    reasons.push("needs email");
  }

  if (roleTerms.some((term) => lead.title.toLowerCase().includes(term))) {
    score += 12;
    reasons.push("buyer title");
  } else if (!lead.contactName && !lead.title) {
    score -= 6;
    reasons.push("needs contact");
  }

  if (
    /outdated|stock|phone photo|missing|no recent|bios|team page|dated|portfolio|generic|headshot|profile|google business|instagram|listing presentation/.test(
      text,
    )
  ) {
    score += 12;
    reasons.push("visual gap");
  }

  if (/franchise|national chain|corporate office/.test(text)) {
    score -= 8;
    reasons.push("unclear local authority");
  }

  if (lead.status === "do_not_contact") {
    score = 0;
    reasons.push("suppressed");
  }

  return {
    score: Math.max(0, Math.min(100, score)),
    reasons: reasons.length ? reasons : ["needs research"],
  };
}

export function firstName(name: string) {
  return name.trim().split(/\s+/)[0] || "";
}

export function companyShort(lead: Lead) {
  return lead.company || "your team";
}

export function createSequence(lead: Lead, campaign: CampaignDefinition, settings: StudioSettings): EmailStep[] {
  const greeting = firstName(lead.contactName) ? `Hi ${firstName(lead.contactName)},` : "Hi there,";
  const company = companyShort(lead);
  const subject = campaign.subject.replace(/\{\{company\}\}/g, company);
  const location = [lead.city, lead.state].filter(Boolean).join(", ");
  const context = [location && `your ${location} market`, lead.industry && `the ${lead.industry.toLowerCase()} space`]
    .filter(Boolean)
    .join(" in ");
  const observation = lead.signals
    ? `I was looking at ${context || "your public presence"} and noted: ${lead.signals}.`
    : `I came across ${company} while reviewing Gwinnett-area businesses that could benefit from stronger visual assets.`;
  const signoff = [
    settings.senderName || "Aiden",
    settings.studioName ? settings.studioName : "",
    settings.market ? `Business photography in ${settings.market}` : "",
    settings.postalAddress,
    "If this is not relevant, reply 'not a fit' and I will not follow up.",
  ]
    .filter(Boolean)
    .join("\n");

  return [
    {
      step: "Email 1",
      subject,
      body: `${greeting}\n\n${observation}\n\nI'm a business and branding photographer focused on local operators in ${settings.market}. ${settings.offer}\n\nIf ${company} could use stronger photos for your website, Google profile, social posts, listing presentations, or referral marketing, I can send over a simple shoot plan and estimate.\n\nWorth a quick look?\n\n${signoff}`,
    },
    {
      step: "Email 2",
      subject: `Re: ${subject}`,
      body: `${greeting}\n\nQuick bump. For realtors, freelancers, and owner-led businesses, better photos can support the places prospects check first: Google, Instagram, LinkedIn, websites, and referral conversations.\n\nIf it helps, I can outline a compact local session for ${company} with the shot list, usage ideas, and rough budget range.\n\n${signoff}`,
    },
    {
      step: "Email 3",
      subject: "Close the loop?",
      body: `${greeting}\n\nI'll close the loop here. If updated headshots, working portraits, neighborhood photos, or brand images become useful later, I'm happy to be a local resource.\n\nWould you prefer I circle back another season, or should I leave it here?\n\n${signoff}`,
    },
  ];
}

export function stableId(lead: Pick<Lead, "company" | "website" | "email" | "city">) {
  const raw = [lead.company, lead.website, lead.email, lead.city].join("|").toLowerCase();
  let hash = 0;
  for (let index = 0; index < raw.length; index += 1) {
    hash = (hash << 5) - hash + raw.charCodeAt(index);
    hash |= 0;
  }
  return `lead_${Math.abs(hash)}`;
}

function normalizeKey(value: string) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function clean(value: unknown) {
  return String(value ?? "").trim();
}

export function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quote = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === '"' && quote && next === '"') {
      field += '"';
      index += 1;
    } else if (char === '"') {
      quote = !quote;
    } else if (char === "," && !quote) {
      row.push(field);
      field = "";
    } else if ((char === "\n" || char === "\r") && !quote) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(field);
      if (row.some((cell) => cell.trim() !== "")) rows.push(row);
      row = [];
      field = "";
    } else {
      field += char;
    }
  }

  row.push(field);
  if (row.some((cell) => cell.trim() !== "")) rows.push(row);
  if (rows.length < 2) return [];

  const headers = rows[0].map(clean);
  return rows.slice(1).map((cells) => {
    const record: Record<string, string> = {};
    headers.forEach((header, index) => {
      record[header || `column_${index + 1}`] = cells[index] ?? "";
    });
    return record;
  });
}

export function mapCsvRow(row: Record<string, string>): Lead {
  const lead: Lead = {
    id: "",
    company: "",
    website: "",
    industry: "",
    city: "",
    state: "",
    contactName: "",
    title: "",
    email: "",
    phone: "",
    linkedin: "",
    notes: "",
    signals: "",
    source: "",
    status: "new",
  };

  Object.entries(row).forEach(([key, value]) => {
    const normalized = normalizeKey(key);
    const match = Object.entries(csvAliases).find(([, aliases]) => aliases.includes(normalized));
    if (!match) return;
    const [field] = match as [keyof Omit<Lead, "id" | "lastTouch">, string[]];
    if (field === "status") {
      lead.status = normalizeStatus(value);
    } else {
      lead[field] = clean(value) as never;
    }
  });

  lead.company = lead.company || "Unnamed business";
  lead.source = lead.source || "CSV import";
  lead.id = stableId(lead);
  return lead;
}

export function normalizeStatus(value: string): LeadStatus {
  const normalized = normalizeKey(value);
  const statuses: LeadStatus[] = ["new", "research", "ready", "sent", "replied", "booked", "do_not_contact"];
  return statuses.find((status) => normalizeKey(status) === normalized) ?? "new";
}

export function csvEscape(value: unknown) {
  const text = String(value ?? "");
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export function toCsv(rows: Record<string, unknown>[]) {
  if (!rows.length) return "";
  const headers = Array.from(
    rows.reduce((set, row) => {
      Object.keys(row).forEach((key) => set.add(key));
      return set;
    }, new Set<string>()),
  );
  return [headers, ...rows.map((row) => headers.map((header) => row[header] ?? ""))]
    .map((line) => line.map(csvEscape).join(","))
    .join("\n");
}

export function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function formatPercent(value: number) {
  return `${Number.isFinite(value) ? value.toFixed(1).replace(/\.0$/, "") : "0"}%`;
}
